import React, { useEffect, useRef } from 'react';

/**
 * McaMenu — Dropdown menu triggered by a button, positioned below trigger.
 * Closes on outside click, Esc key, or item click.
 * 
 * Props:
 *   - trigger (node): Trigger element (button). Component wraps it.
 *   - open (bool): Control menu visibility
 *   - onClose (fn): Called when menu closes
 *   - children (node): Menu items (use <McaMenuItem> or raw <button role="menuitem">)
 *   - placement (str): 'below' (default) or 'auto' — positioning hint
 */
export function McaMenu({ trigger, open, onClose, children, placement = 'below' }) {
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        onClose?.();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    // Use capture phase to catch clicks before they bubble
    setTimeout(() => {
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open, onClose]);

  // Position menu below trigger
  useEffect(() => {
    if (!open || !triggerRef.current || !menuRef.current) return;

    const positionMenu = () => {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const menu = menuRef.current;

      // Default: position below trigger, right-aligned
      menu.style.position = 'fixed';
      menu.style.top = (triggerRect.bottom + 6) + 'px';
      menu.style.right = (window.innerWidth - triggerRect.right) + 'px';
      menu.style.left = 'auto';
      menu.style.zIndex = '300';
    };

    positionMenu();
    window.addEventListener('scroll', positionMenu);
    window.addEventListener('resize', positionMenu);

    return () => {
      window.removeEventListener('scroll', positionMenu);
      window.removeEventListener('resize', positionMenu);
    };
  }, [open]);

  const handleTriggerClick = (e) => {
    if (trigger && trigger.props && trigger.props.onClick) {
      trigger.props.onClick(e);
    }
  };

  const handleItemClick = () => {
    onClose?.();
  };

  return (
    <div
      ref={wrapperRef}
      className={`mca-menu-wrap${open ? " is-open" : ""}`}
      data-menu
    >
      {/* Trigger button — clone with refs and click handler */}
      {trigger && React.cloneElement(trigger, {
        ref: triggerRef,
        'data-menu-trigger': true,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        onClick: handleTriggerClick
      })}

      {/* Menu dropdown */}
      {open && (
        <div
          ref={menuRef}
          className="mca-menu"
          role="menu"
          onClick={handleItemClick}
        >
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * McaMenuItem — Individual menu item (button or link).
 */
export function McaMenuItem({ children, onClick, isDanger, ...props }) {
  return (
    <button
      className={`mca-menu__item ${isDanger ? 'mca-menu__item--danger' : ''}`}
      role="menuitem"
      onClick={onClick}
      type="button"
      {...props}
    >
      {children}
    </button>
  );
}

/**
 * McaMenuSeparator — Visual divider in menu.
 */
export function McaMenuSeparator() {
  return <div className="mca-menu__sep" role="separator" />;
}

export default McaMenu;
